/**
 * Upay reveal — turns the chart's strongest detected dosha into the offer card's
 * lead, so the card speaks to THIS seeker instead of a generic "Premium Report".
 *
 * The shape follows the rule-22 brand formula: pain question → the chart names the
 * pattern → one free upay, with the full upay sequence gated behind the report.
 *
 * Fact discipline (rules 24/29): the dosha NAME + HOUSE in the eyebrow come from
 * the ENGINE (`Dosha.name_en` / `triggering_house`), never a literal. The pain
 * headline is curated cultural prose (no computed value embedded), and the free
 * upay is REUSED from the dosha explainer catalog (`EXPLAINER_CATALOG_DOSHA`) so
 * there is no second, drifting copy of the remedy text (rules 23/24).
 *
 * Returns null when the chart carries no present dosha we have curated framing for
 * — the card then keeps its existing generic copy. Display-only: it never changes
 * which offer is shown or the offer order (that stays governed, rule 40).
 */

import { EXPLAINER_CATALOG_DOSHA } from "./explainer-catalog-dosha-data";
import type { KundaliResponse } from "./api/kundali";

type Dosha = KundaliResponse["doshas"][number];
type Lang = "en" | "hi";

export interface UpayReveal {
  /** "Your chart · Mangal Dosh · 7th house" — name + house from the engine. */
  eyebrow: string;
  /** Rule-22 pain question that opens the brand formula. Curated, fact-free prose. */
  painHeadline: string;
  /** One free upay, reused from the dosha explainer catalog (Hinglish on /hi
   * via the catalog's `title_hi` arm; degrades to English when absent). */
  freeUpayTitle: string;
  /** Short "why" for the free upay in the seeker's language (`why_hi` on /hi;
   * omitted when the catalog carries no arm for that language). */
  freeUpayWhy?: string;
}

/**
 * Each concept maps the engine's dosha id (matched by token, mirroring
 * post-kundali-cta's `doshaRuleFor`) to (a) the explainer-catalog id that owns its
 * upays and (b) the rule-22 pain headline. Order matters: "sade" is matched before
 * "shani" so Sade Sati keeps its own headline while sharing Shani's upays.
 */
const CONCEPTS: {
  tokens: string[];
  catalog: string;
  headline: Record<Lang, string>;
}[] = [
  {
    tokens: ["mangal"],
    catalog: "mangal_dosh",
    headline: {
      en: "Why do your closest relationships keep hitting the same wall?",
      hi: "Aapke kareebi rishton mein baar-baar wahi rukawat kyun aati hai?",
    },
  },
  {
    tokens: ["kaal", "sarp"],
    catalog: "kaal_sarp_dosh",
    headline: {
      en: "Why does every effort seem to stall just before the breakthrough?",
      hi: "Har mehnat kaamyaabi se theek pehle kyun ruk jaati hai?",
    },
  },
  {
    tokens: ["pitra"],
    catalog: "pitra_dosh",
    headline: {
      en: "Why does the same misfortune keep following your family?",
      hi: "Wahi pareshani baar-baar aapke parivaar ke peeche kyun aati hai?",
    },
  },
  {
    tokens: ["sade"],
    catalog: "shani_dosh",
    headline: {
      en: "Why does life feel like it's pushing uphill right now?",
      hi: "Abhi zindagi har kadam par bhaari kyun lag rahi hai?",
    },
  },
  {
    tokens: ["shani"],
    catalog: "shani_dosh",
    headline: {
      en: "Why does your hard work keep getting delayed and tested?",
      hi: "Aapki mehnat baar-baar der aur imtihaan se kyun guzarti hai?",
    },
  },
  {
    tokens: ["rahu"],
    catalog: "rahu_dosh",
    headline: {
      en: "Why won't your mind switch off, even when you're drained?",
      hi: "Thakaan ke baad bhi dimaag shaant kyun nahi hota?",
    },
  },
  {
    tokens: ["ketu"],
    catalog: "ketu_dosh",
    headline: {
      en: "Why do you feel pulled away from the things that should matter most?",
      hi: "Jo cheezein maayne rakhti hain, unse mann kyun hat jaata hai?",
    },
  },
  {
    tokens: ["chandal"],
    catalog: "guru_chandal_dosh",
    headline: {
      en: "Why does your judgement keep leading you the wrong way?",
      hi: "Aapke faisle baar-baar galat disha mein kyun le jaate hain?",
    },
  },
  {
    tokens: ["kemadruma"],
    catalog: "kemadruma_yoga",
    headline: {
      en: "Why does support disappear exactly when you need it most?",
      hi: "Madad theek tab kyun gayab ho jaati hai jab sabse zyada zaroorat ho?",
    },
  },
];

type Concept = (typeof CONCEPTS)[number];
type CatalogUpay = { title: string; why?: string; displayName: string };

const ENGLISH_ORDINALS = [
  "",
  "1st",
  "2nd",
  "3rd",
  "4th",
  "5th",
  "6th",
  "7th",
  "8th",
  "9th",
  "10th",
  "11th",
  "12th",
];

function ordinalHouse(house: number): string {
  return ENGLISH_ORDINALS[house] ?? `${house}th`;
}

function firstSentence(text: string): string {
  const clean = text.replace(/\s+/g, " ").trim();
  const end = clean.indexOf(". ");
  return end === -1 ? clean : clean.slice(0, end + 1);
}

/** First concept whose token appears in the engine dosha id, or null. */
function findConcept(doshaId: string): Concept | null {
  const id = doshaId.toLowerCase();
  return CONCEPTS.find((c) => c.tokens.some((token) => id.includes(token))) ?? null;
}

/** The first curated upay for a catalog id (the free teaser) in the seeker's
 * language, or null. On /hi the Hinglish arm (`title_hi`/`why_hi`) is used;
 * a missing hi title degrades to English, and a missing hi "why" is omitted
 * rather than shown in the wrong language. */
function catalogUpay(catalogId: string, lang: Lang): CatalogUpay | null {
  const entry = EXPLAINER_CATALOG_DOSHA.find((e) => e.id === catalogId);
  const upay = entry?.upays?.[0];
  if (!entry || !upay) return null;
  if (lang === "hi") {
    return {
      title: upay.title_hi ?? upay.title,
      why: upay.why_hi,
      displayName: entry.display_name,
    };
  }
  return { title: upay.title, why: upay.why, displayName: entry.display_name };
}

/** " · 7th house" when the engine reports a 1–12 triggering house, else "". */
function houseSuffix(house: number | null | undefined): string {
  if (typeof house !== "number" || house < 1 || house > 12) return "";
  return ` · ${ordinalHouse(house)} house`;
}

function eyebrowFor(name: string, house: number | null | undefined, lang: Lang): string {
  const prefix = lang === "hi" ? "Aapki kundali" : "Your chart";
  return `${prefix} · ${name}${houseSuffix(house)}`;
}

function resolveName(dosha: Dosha, fallback: string, lang: Lang): string {
  const engineName = lang === "hi" ? dosha.name_hi : dosha.name_en;
  return engineName || fallback;
}

/**
 * Build the upay-reveal framing for a chart's strongest present dosha, or null if
 * the dosha is absent / not one we carry curated framing for (keep the generic card).
 */
export function buildUpayReveal(
  dosha: Dosha | null | undefined,
  language: string
): UpayReveal | null {
  if (!dosha || !dosha.is_present) return null;
  const concept = findConcept(dosha.dosha_id ?? "");
  if (!concept) return null;
  const lang: Lang = language === "hi" ? "hi" : "en";
  const upay = catalogUpay(concept.catalog, lang);
  if (!upay) return null;

  const name = resolveName(dosha, upay.displayName, lang);
  return {
    eyebrow: eyebrowFor(name, dosha.triggering_house, lang),
    painHeadline: concept.headline[lang],
    freeUpayTitle: upay.title,
    freeUpayWhy: upay.why ? firstSentence(upay.why) : undefined,
  };
}

/** The present dosha in a chart whose id matches a token (e.g. "mangal", "pitra"). */
export function findDoshaForReveal(
  doshas: Dosha[] | null | undefined,
  token: string
): Dosha | null {
  const t = token.toLowerCase();
  return (
    (doshas ?? []).find((d) => d.is_present && (d.dosha_id ?? "").toLowerCase().includes(t)) ?? null
  );
}

/**
 * A minimal dosha object for surfaces that compute a dosha OUTSIDE the standard
 * `Dosha[]` array (e.g. the Kaal Sarp checker returns its own response shape, the
 * matching result carries a Mangal status). buildUpayReveal only reads
 * id/name/presence/severity/house, so this carries exactly those.
 */
export function syntheticDosha(args: {
  doshaId: string;
  nameEn: string;
  nameHi: string;
  isPresent: boolean;
  severity: number;
  triggeringHouse?: number | null;
}): Dosha {
  return {
    dosha_id: args.doshaId,
    name_en: args.nameEn,
    name_hi: args.nameHi,
    is_present: args.isPresent,
    severity: args.severity,
    triggering_house: args.triggeringHouse ?? null,
    triggering_planet: null,
    description: "",
  } as Dosha;
}

/** Single source for the upay-reveal unlock CTA label (rules 23/24 — no drift). */
export function upayUnlockCtaLabel(priceInr: number, language: string): string {
  return language === "hi"
    ? `Poora upay paayein - ₹${priceInr}`
    : `Unlock your full upay - ₹${priceInr}`;
}
